import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { hasFullAccess, isNativePlatform } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  LogOut,
  Users,
  Crown,
  Briefcase,
  Banknote,
  Scale,
  Wrench,
  Target,
  Bot,
  Search,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Shield,
  Star,
  Gift,
  Calendar,
  FileText,
  Plus,
  User,
  PenTool,
  CheckCircle,
  Euro,
  Loader2,
  Video,
  Sparkles,
  Award,
  Network,
  Handshake,
  TrendingUp,
  ArrowRight,
  Play,
  Edit,
  Trash2,
  X
} from 'lucide-react';

// Professional partners data (unchanged)

// Professional categories with enhanced styling
const PRO_CATEGORIES = [
  {
    id: 'financement',
    title: 'Financement & Assurances',
    icon: Banknote,
    color: 'from-emerald-500 to-green-600',
    bgColor: 'bg-emerald-500',
    description: 'Courtier prêt, Assurances, Épargne et Protection...'
  },
  {
    id: 'juridique',
    title: 'Juridique, Fiscal & Patrimoine',
    icon: Scale,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500',
    description: 'Notaires, Expert-comptable, CGP, Avocate immo...'
  },
  {
    id: 'travaux',
    title: 'Travaux & Technique',
    icon: Wrench,
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500',
    description: 'Contractant, Architecte, Diagnostiqueur, Géomètre...'
  },
  {
    id: 'strategie',
    title: 'Stratégie & Acquisition',
    icon: Target,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500',
    description: 'Conseiller en investissement, Marchand de biens, Agent immobilier...'
  },
  {
    id: 'tech',
    title: 'Tech / IA / Réseaux sociaux',
    icon: Bot,
    color: 'from-cyan-500 to-teal-600',
    bgColor: 'bg-cyan-500',
    description: 'IA, Création digitale, Application web, Website...'
  }
];

// Premium Partner Card Component
const PartnerCard = ({ partner, categoryColor }) => {
  const [expanded, setExpanded] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl bg-slate-800/50 border transition-all duration-300 ${
        partner.isFounder 
          ? 'border-amber-500/50 ring-1 ring-amber-500/30' 
          : 'border-slate-700/50 hover:border-violet-500/50'
      }`}
    >
      {/* Founder Badge Ribbon */}
      {partner.isFounder && (
        <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden">
          <div className="absolute top-4 right-[-35px] w-[170px] transform rotate-45 bg-gradient-to-r from-amber-500 to-orange-500 text-center py-1 text-xs font-bold text-white shadow-lg">
            FONDATEUR
          </div>
        </div>
      )}

      {/* Card Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-3 sm:p-5 flex items-start gap-3 sm:gap-4"
        data-testid={`partner-card-${partner.id}`}
      >
        {/* Avatar */}
        <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden ${
          partner.isFounder 
            ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
            : 'bg-gradient-to-br from-violet-500 to-purple-600'
        }`}>
          {partner.id === 'nicolas-llorens' ? (
            <img 
              src="https://customer-assets.emergentagent.com/job_b644cc1a-aab6-42fe-94c2-7ef2c35ac822/artifacts/f0h172kj_Navy%20and%20Blue%20Abstract%20Modern%20Globe%20Technology%20Logo%20Design.png" 
              alt="Webisula"
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="font-bold text-white text-base sm:text-lg">{partner.name}</h4>
            {partner.company && (
              <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">{partner.company}</span>
            )}
          </div>
          <p className="text-violet-400 font-medium text-xs sm:text-sm mb-1.5 sm:mb-2">{partner.profession}</p>
          {partner.city && (
            <p className="text-slate-500 text-xs flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {partner.city}
            </p>
          )}
          {/* Advantage Badge - Redesigned */}
          <div className="mt-2 sm:mt-3 inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-lg bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-emerald-500 rounded-md flex items-center justify-center flex-shrink-0">
              <Euro className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
            </div>
            <span className="text-emerald-300 text-xs sm:text-sm font-semibold">{partner.advantage}</span>
          </div>
        </div>

        {/* Expand Icon */}
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
          expanded ? 'bg-violet-500/20' : 'bg-slate-700/50'
        }`}>
          {expanded ? (
            <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400" />
          ) : (
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="px-3 sm:px-5 pb-4 sm:pb-5 space-y-3 sm:space-y-4 border-t border-slate-700/50"
        >
          <div className="pt-3 sm:pt-4 grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2">
            {/* When */}
            <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4">
              <h5 className="font-semibold text-white text-xs sm:text-sm flex items-center gap-2 mb-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" />
                Quand faire appel ?
              </h5>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">{partner.when}</p>
            </div>

            {/* Prepare */}
            <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4">
              <h5 className="font-semibold text-white text-xs sm:text-sm flex items-center gap-2 mb-2">
                <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400" />
                À préparer
              </h5>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">{partner.prepare}</p>
            </div>
          </div>

          {/* Video */}
          {partner.videoUrl && (
            <div className="pt-2">
              <h5 className="font-semibold text-white text-sm flex items-center gap-2 mb-3">
                <Video className="w-4 h-4 text-purple-400" />
                Vidéo de présentation
              </h5>
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video shadow-2xl ring-1 ring-white/10">
                <video 
                  controls 
                  className="w-full h-full object-contain"
                  poster=""
                  preload="metadata"
                >
                  <source src={partner.videoUrl} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
            </div>
          )}

          {/* Contact Actions */}
          <div className="pt-2 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
            {partner.email && (
              <a
                href={`mailto:${partner.email}`}
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl text-xs sm:text-sm font-medium hover:from-violet-600 hover:to-purple-700 transition-all shadow-lg shadow-violet-500/25 justify-center"
              >
                <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{partner.email}</span>
              </a>
            )}
            {partner.phone && (
              <a
                href={`tel:${partner.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl text-xs sm:text-sm font-medium hover:from-emerald-600 hover:to-green-700 transition-all shadow-lg shadow-emerald-500/25 justify-center"
              >
                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                {partner.phone}
              </a>
            )}
            {partner.link && (
              <a
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-700 text-white rounded-xl text-xs sm:text-sm font-medium hover:bg-slate-600 transition-all justify-center"
              >
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                Prendre RDV
              </a>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const ProsDirectoryPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPartnershipForm, setShowPartnershipForm] = useState(false);
  const [submittingPartnership, setSubmittingPartnership] = useState(false);
  const [partnersData, setPartnersData] = useState({});
  const [partnersCategories, setPartnersCategories] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [partnershipForm, setPartnershipForm] = useState({
    company_name: '',
    contact_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    profession: '',
    category: '',
    other_category: '',
    city: '',
    description: '',
    website: '',
    has_insurance: false,
    accepts_conditions: false
  });

  const isClubMember = hasFullAccess(user?.subscription_tier);
  const isFounder = user?.email === 'rajasalame@gmail.com';

  const fetchPartners = async () => {
    try {
      const r = await axios.get(`${API}/directory/partners`);
      setPartnersData(r.data?.partners || {});
      setPartnersCategories(r.data?.categories || []);
    } catch (e) {
      console.error('[ProsDirectory] Failed:', e);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  const openEditPartner = (partner, defaultCategory = null) => {
    setEditingPartner(partner ? { ...partner } : {
      id: null, name: '', company: '', profession: '', when: '',
      prepare: '', advantage: '', email: '', phone: '', videoUrl: '',
      category: defaultCategory || PRO_CATEGORIES[0]?.id || 'financement'
    });
    setShowEditModal(true);
  };

  const savePartner = async () => {
    if (!editingPartner?.name?.trim()) return;
    setEditSaving(true);
    try {
      const payload = { ...editingPartner };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.order;
      if (editingPartner.id) {
        await axios.put(`${API}/directory/admin/partners/${editingPartner.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/directory/admin/partners`, payload, { withCredentials: true });
      }
      setShowEditModal(false);
      setEditingPartner(null);
      await fetchPartners();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setEditSaving(false);
    }
  };

  const deletePartner = async (partner) => {
    if (!window.confirm(`Supprimer définitivement "${partner.name}" ?`)) return;
    try {
      await axios.delete(`${API}/directory/admin/partners/${partner.id}`, { withCredentials: true });
      await fetchPartners();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  // Count total partners
  const totalPartners = Object.values(partnersData).reduce((sum, arr) => sum + arr.length, 0);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setPartnershipForm(prev => ({
        ...prev,
        contact_name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handlePartnershipSubmit = async () => {
    if (!partnershipForm.company_name || !partnershipForm.contact_name || !partnershipForm.email || 
        !partnershipForm.phone || !partnershipForm.profession || !partnershipForm.category || 
        !partnershipForm.city || !partnershipForm.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // If "Autre" is selected, other_category is required
    if (partnershipForm.category === 'Autre' && !partnershipForm.other_category) {
      toast.error('Veuillez préciser votre catégorie');
      return;
    }
    
    if (!partnershipForm.accepts_conditions) {
      toast.error('Vous devez accepter les conditions du partenariat');
      return;
    }

    setSubmittingPartnership(true);
    try {
      // Build final category
      const finalForm = {
        ...partnershipForm,
        category: partnershipForm.category === 'Autre' ? partnershipForm.other_category : partnershipForm.category
      };
      await axios.post(`${API}/partnership/request`, finalForm, { withCredentials: true });
      toast.success('Votre demande de partenariat a été envoyée avec succès !');
      setShowPartnershipForm(false);
      setPartnershipForm({
        company_name: '',
        contact_name: user?.name || '',
        email: user?.email || '',
        phone: '',
        profession: '',
        category: '',
        other_category: '',
        city: '',
        description: '',
        website: '',
        has_insurance: false,
        accepts_conditions: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmittingPartnership(false);
    }
  };

  const filterPartners = (partners) => {
    if (!searchQuery) return partners;
    const query = searchQuery.toLowerCase();
    return partners.filter(p => 
      p.name.toLowerCase().includes(query) ||
      p.profession.toLowerCase().includes(query) ||
      (p.company && p.company.toLowerCase().includes(query))
    );
  };

  // Upgrade Screen for non-members
  if (!isClubMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-violet-950 to-slate-950">
        {/* Header */}
        <header className="relative z-10 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Tableau de bord
                </Button>
              </Link>
            </div>
          </div>
        </header>

        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] p-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg w-full"
          >
            <div className="relative overflow-hidden rounded-3xl bg-slate-900/80 border border-violet-500/30 backdrop-blur-xl shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 to-purple-500/10" />
              <div className="relative p-8 text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-violet-500/30">
                  <Briefcase className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Annuaire Pros Partenaires</h2>
                <p className="text-slate-400 mb-6">Fonctionnalité exclusive du Club 10M Investor</p>
                
                <div className="space-y-3 text-left mb-6">
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <Star className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">+{totalPartners} professionnels vérifiés</p>
                      <p className="text-sm text-slate-400">Accédez à notre réseau de partenaires de confiance</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <Gift className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">Avantages exclusifs</p>
                      <p className="text-sm text-slate-400">5-10% de réduction réservés aux membres</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-slate-800/50 rounded-xl p-4">
                    <Shield className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">5 catégories d'experts</p>
                      <p className="text-sm text-slate-400">Financement, Juridique, Travaux, Stratégie, Tech</p>
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white py-6 text-lg font-semibold shadow-lg shadow-violet-500/25"
                  onClick={() => !isNativePlatform() && navigate('/pricing')}
                >
                  <Crown className="w-5 h-5 mr-2" />
                  {isNativePlatform() ? 'Bientôt disponible' : 'Rejoindre le Club 10M Investor'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-violet-950 to-slate-950">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/10 px-2 sm:px-3">
                <ChevronLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Tableau de bord</span>
              </Button>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover ring-2 ring-violet-500/50" />
              ) : (
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs sm:text-sm font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-medium text-white hidden sm:block">{user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative z-10 pt-6 sm:pt-12 pb-6 sm:pb-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Badge */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-4 sm:mb-6"
          >
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs sm:text-sm font-medium">
              <Network className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              RÉSEAU EXCLUSIF • {totalPartners} PARTENAIRES
            </span>
          </motion.div>

          {/* Title */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-6 sm:mb-8"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-3 sm:mb-4">
              <span className="text-white">PROS</span>
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-violet-400 bg-clip-text text-transparent ml-2 sm:ml-3">PARTENAIRES</span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-lg max-w-2xl mx-auto px-2">
              Des professionnels de confiance sélectionnés pour accompagner vos projets immobiliers. 
              Avantages exclusifs réservés aux membres du Club.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6 sm:mb-10"
          >
            <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-white">{totalPartners}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Experts</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-white">5-10%</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Réduction</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl bg-slate-800/50 border border-slate-700/50">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-white">5</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Catégories</p>
              </div>
            </div>
          </motion.div>

          {/* Search */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl mx-auto px-1"
          >
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
              <Input
                type="text"
                placeholder="Rechercher un professionnel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 sm:pl-12 h-11 sm:h-14 text-sm sm:text-base bg-slate-800/50 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl sm:rounded-2xl focus:ring-violet-500 focus:border-violet-500"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Categories */}
      <div className="relative z-10 max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-3 sm:space-y-4">
          {PRO_CATEGORIES.map((category, index) => {
            const IconComponent = category.icon;
            const isExpanded = expandedCategory === category.id;
            const partners = partnersData[category.id] || [];
            const filteredPartners = filterPartners(partners);
            
            return (
              <motion.div 
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="overflow-hidden rounded-2xl bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full text-left"
                  data-testid={`category-${category.id}`}
                >
                  <div className={`relative overflow-hidden p-3 sm:p-5 flex items-center justify-between bg-gradient-to-r ${category.color}`}>
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="relative flex items-center space-x-2.5 sm:space-x-4 flex-1 min-w-0">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-white text-base sm:text-xl truncate">{category.title}</h3>
                        <p className="text-white/70 text-xs sm:text-sm truncate">{partners.length} partenaire{partners.length > 1 ? 's' : ''} • {category.description}</p>
                      </div>
                    </div>
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                      isExpanded ? 'bg-white/30' : 'bg-white/10'
                    }`}>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      ) : (
                        <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Partners List */}
                {isExpanded && (
                  <div className="p-5 space-y-4">
                    {filteredPartners.length > 0 ? (
                      filteredPartners.map((partner) => (
                        <div key={partner.id} className="relative group">
                          <PartnerCard partner={partner} categoryColor={category.color} />
                          {isFounder && (
                            <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditPartner(partner); }}
                                className="p-2 bg-white/90 rounded-md shadow hover:bg-white text-slate-700 hover:text-blue-600"
                                title="Modifier"
                                data-testid={`edit-partner-${partner.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); deletePartner(partner); }}
                                className="p-2 bg-white/90 rounded-md shadow hover:bg-white text-slate-700 hover:text-red-600"
                                title="Supprimer"
                                data-testid={`delete-partner-${partner.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-slate-500">Aucun partenaire trouvé</p>
                      </div>
                    )}

                    {isFounder && (
                      <Button
                        variant="outline"
                        className="w-full border-dashed border-2 border-blue-500/50 text-blue-400 hover:text-blue-300 hover:border-blue-400 hover:bg-blue-500/10 rounded-xl py-3"
                        onClick={() => openEditPartner(null, category.id)}
                        data-testid={`add-partner-${category.id}`}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un partenaire dans cette catégorie
                      </Button>
                    )}
                    
                    {/* Become partner button */}
                    <div className="pt-4 border-t border-slate-700/50">
                      <Button
                        variant="outline"
                        className="w-full border-dashed border-2 border-violet-500/50 text-violet-400 hover:text-violet-300 hover:border-violet-400 hover:bg-violet-500/10 rounded-xl py-6"
                        onClick={() => {
                          setPartnershipForm(prev => ({ ...prev, category: category.title }));
                          setShowPartnershipForm(true);
                        }}
                        data-testid={`become-partner-${category.id}`}
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Devenir Partenaire Pro dans cette catégorie
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-purple-700" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi00LTItNC0yLTQtMi0yIDItMiA0IDIgNCAyIDQgNCAyIDQgMiAyLTIgMi00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
            <div className="relative p-8 sm:p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
                <Handshake className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Vous êtes un professionnel de l'immobilier ?
              </h3>
              <p className="text-violet-100 mb-6 max-w-xl mx-auto">
                Rejoignez notre réseau de partenaires et accédez à une communauté d'investisseurs qualifiés. 
                Développez votre activité avec le Club 10M Investor.
              </p>
              <Button 
                size="lg"
                className="bg-white text-violet-700 hover:bg-violet-50 font-semibold px-8 py-6 text-lg rounded-xl shadow-xl"
                onClick={() => setShowPartnershipForm(true)}
                data-testid="become-partner-footer"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Devenir Partenaire Pro
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Partnership Form Dialog */}
      <Dialog open={showPartnershipForm} onOpenChange={setShowPartnershipForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2 text-white">
              <Crown className="w-6 h-6 text-amber-500" />
              Devenir Partenaire Pro
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Rejoignez notre réseau de professionnels partenaires et développez votre activité auprès d'investisseurs qualifiés.
            </DialogDescription>
          </DialogHeader>

          {/* Pricing Info - hidden on mobile (Apple 3.1.1) */}
          {!isNativePlatform() && (
          <div className="bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30 rounded-xl p-4 space-y-3">
            <h4 className="font-semibold text-white flex items-center gap-2">
              <Euro className="w-5 h-5 text-violet-400" />
              Conditions du partenariat Pro
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <p className="text-violet-400 font-semibold text-lg">1 500€ HT/an</p>
                <p className="text-slate-500">Abonnement annuel</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <p className="text-violet-400 font-semibold text-lg">12 mois</p>
                <p className="text-slate-500">Engagement minimum</p>
              </div>
            </div>
            <div className="space-y-2 mt-3">
              <p className="text-sm font-medium text-slate-300">Obligations du partenaire :</p>
              <ul className="text-sm text-slate-400 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span><strong className="text-white">5 à 10% de réduction</strong> sur vos tarifs pour les membres</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span><strong className="text-white">1 podcast par an</strong> minimum pour présenter votre expertise</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span><strong className="text-white">1 événement par an</strong> minimum (participation aux rencontres)</span>
                </li>
              </ul>
            </div>
          </div>
          )}

          {/* Form */}
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-slate-300">Nom de l'entreprise *</Label>
                <Input
                  id="company_name"
                  placeholder="Ex: Mon Cabinet Conseil"
                  value={partnershipForm.company_name}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, company_name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_name" className="text-slate-300">Nom du contact *</Label>
                <Input
                  id="contact_name"
                  placeholder="Prénom Nom"
                  value={partnershipForm.contact_name}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, contact_name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="partner_email" className="text-slate-300">Email professionnel *</Label>
                <Input
                  id="partner_email"
                  type="email"
                  placeholder="contact@entreprise.com"
                  value={partnershipForm.email}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, email: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-300">Téléphone *</Label>
                <Input
                  id="phone"
                  placeholder="06 XX XX XX XX"
                  value={partnershipForm.phone}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, phone: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profession" className="text-slate-300">Profession / Métier *</Label>
                <Input
                  id="profession"
                  placeholder="Ex: Courtier en crédit"
                  value={partnershipForm.profession}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, profession: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category" className="text-slate-300">Catégorie souhaitée *</Label>
                <Select 
                  value={partnershipForm.category} 
                  onValueChange={(value) => {
                    setPartnershipForm(prev => ({ 
                      ...prev, 
                      category: value, 
                      other_category: value === 'Autre' ? prev.other_category : '' 
                    }));
                  }}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 z-[10000]">
                    {PRO_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.title} className="text-white hover:bg-slate-700">{cat.title}</SelectItem>
                    ))}
                    <SelectItem value="Autre" className="text-white hover:bg-slate-700">
                      ✨ Autre catégorie...
                    </SelectItem>
                  </SelectContent>
                </Select>
                {partnershipForm.category === 'Autre' && (
                  <Input
                    placeholder="Précisez votre catégorie..."
                    value={partnershipForm.other_category || ''}
                    onChange={(e) => setPartnershipForm(prev => ({ ...prev, other_category: e.target.value }))}
                    className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-slate-300">Ville *</Label>
                <Input
                  id="city"
                  placeholder="Ex: Paris, Lyon, Marseille..."
                  value={partnershipForm.city}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, city: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website" className="text-slate-300">Site web (optionnel)</Label>
                <Input
                  id="website"
                  placeholder="https://www.monsite.fr"
                  value={partnershipForm.website}
                  onChange={(e) => setPartnershipForm({ ...partnershipForm, website: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-slate-300">Description de votre activité *</Label>
              <Textarea
                id="description"
                placeholder="Décrivez votre activité, votre expertise et ce que vous pouvez apporter aux membres de la communauté..."
                rows={4}
                value={partnershipForm.description}
                onChange={(e) => setPartnershipForm({ ...partnershipForm, description: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-400" />
                <div>
                  <Label htmlFor="has_insurance" className="font-medium text-white">Assurance RC Professionnelle</Label>
                  <p className="text-sm text-slate-500">Êtes-vous couvert par une assurance RC Pro ?</p>
                </div>
              </div>
              <Switch
                id="has_insurance"
                checked={partnershipForm.has_insurance}
                onCheckedChange={(checked) => setPartnershipForm({ ...partnershipForm, has_insurance: checked })}
              />
            </div>

            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <input
                type="checkbox"
                id="accepts_conditions"
                checked={partnershipForm.accepts_conditions}
                onChange={(e) => setPartnershipForm({ ...partnershipForm, accepts_conditions: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <label htmlFor="accepts_conditions" className="text-sm text-amber-200">
                {isNativePlatform() 
                  ? "J'accepte les conditions du partenariat Pro et je m'engage à offrir une réduction aux membres et participer aux événements."
                  : <>J'accepte les conditions du partenariat Pro : abonnement de <strong>1 500€ HT/an</strong> avec engagement de <strong>12 mois</strong>, et je m'engage à offrir <strong>5 à 10% de réduction</strong> aux membres, participer à <strong>1 podcast</strong> et <strong>1 événement</strong> par an minimum.</>
                }
              </label>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPartnershipForm(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Annuler
            </Button>
            <Button 
              onClick={handlePartnershipSubmit} 
              disabled={submittingPartnership || !partnershipForm.accepts_conditions}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              {submittingPartnership ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer ma demande
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit/Create Partner Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>{editingPartner?.id ? 'Modifier le partenaire' : 'Ajouter un partenaire'}</DialogTitle>
          </DialogHeader>
          {editingPartner && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">Catégorie *</label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  value={editingPartner.category || ''}
                  onChange={e => setEditingPartner({ ...editingPartner, category: e.target.value })}
                  data-testid="edit-partner-category"
                >
                  {partnersCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              {[
                { key: 'name', label: 'Nom complet *', placeholder: 'Prénom NOM', full: true },
                { key: 'company', label: 'Entreprise', placeholder: 'Ex: AG Conseils' },
                { key: 'profession', label: 'Profession', placeholder: 'Ex: Courtier prêt' },
                { key: 'email', label: 'Email', placeholder: 'contact@exemple.fr' },
                { key: 'phone', label: 'Téléphone', placeholder: '06 XX XX XX XX' },
                { key: 'advantage', label: 'Avantage Club', placeholder: 'Ex: 10% de réduction', full: true },
                { key: 'when', label: 'Quand le contacter', placeholder: '', full: true, textarea: true },
                { key: 'prepare', label: 'Documents à préparer', placeholder: '', full: true, textarea: true },
                { key: 'videoUrl', label: 'URL de la vidéo (podcast)', placeholder: 'https://...', full: true },
              ].map(f => (
                <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">{f.label}</label>
                  {f.textarea ? (
                    <textarea
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none"
                      rows={2}
                      value={editingPartner[f.key] || ''}
                      onChange={e => setEditingPartner({ ...editingPartner, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      data-testid={`edit-partner-field-${f.key}`}
                    />
                  ) : (
                    <input
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                      type="text"
                      value={editingPartner[f.key] || ''}
                      onChange={e => setEditingPartner({ ...editingPartner, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      data-testid={`edit-partner-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={editSaving}>
                  Annuler
                </Button>
                <Button
                  onClick={savePartner}
                  disabled={editSaving || !editingPartner.name?.trim()}
                  className="bg-[#0D47A1] hover:bg-[#0A367A]"
                  data-testid="save-partner-btn"
                >
                  {editSaving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProsDirectoryPage;
